import { ReactElement, useEffect, useRef, HTMLAttributes } from 'react';
import { useTheme } from '@mui/material';
import { ColorToken, HexColor } from '../../style/types';
import { getColor } from '../../style/utils';

type Props = HTMLAttributes<HTMLDivElement> & {
  children: ReactElement;
  width?: number | string;
  height?: number | string;
  className?: string;
  fillColor?: ColorToken | HexColor;
  strokeColor?: ColorToken | HexColor;
  strokeWidth?: number;
};

const isHexColor = (color: ColorToken | HexColor): color is HexColor =>
  color.startsWith('#');

const SvgIcon = ({
  children,
  width,
  height,
  className,
  fillColor,
  strokeColor,
  strokeWidth,
  style,
  ...rest
}: Props) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const resolveColor = (color: ColorToken | HexColor) =>
      isHexColor(color) ? color : getColor(theme, color);

    const container = containerRef.current;
    if (!container) return;

    const svgElement = container.querySelector('svg');
    if (!svgElement) return;

    const originalWidth = svgElement.getAttribute('width');
    const originalHeight = svgElement.getAttribute('height');

    if (width !== undefined || height !== undefined) {
      const originalViewBox = svgElement.getAttribute('viewBox');

      // If no viewBox exists, create one from the original width/height
      if (!originalViewBox) {
        const viewBoxWidth = originalWidth || 0;
        const viewBoxHeight = originalHeight || 0;
        svgElement.setAttribute(
          'viewBox',
          `0 0 ${viewBoxWidth} ${viewBoxHeight}`,
        );
      }

      svgElement.removeAttribute('width');
      svgElement.removeAttribute('height');
      svgElement.setAttribute('preserveAspectRatio', 'xMidYMid meet');

      const finalWidth = typeof width === 'number' ? `${width}px` : width;
      const finalHeight =
        typeof height === 'number' ? `${height}px` : height || finalWidth;

      if (finalWidth) {
        svgElement.style.width = finalWidth;
      }
      if (finalHeight) {
        svgElement.style.height = finalHeight;
      }

      // Ensure the SVG is block-level for proper sizing
      svgElement.style.display = 'block';
    }

    if (fillColor || strokeColor) {
      const pathElements = svgElement.querySelectorAll(
        'path, circle, rect, polygon, ellipse, line, polyline',
      );
      pathElements.forEach((pathElement) => {
        if (fillColor) {
          (pathElement as SVGElement).style.fill = resolveColor(fillColor);
        }
        if (strokeColor) {
          (pathElement as SVGElement).style.stroke = resolveColor(strokeColor);
        }
        if (strokeWidth) {
          (pathElement as SVGElement).style.strokeWidth =
            strokeWidth.toString();
        }
      });
    }
  }, [width, height, fillColor, strokeColor, strokeWidth, theme]);

  return (
    <div
      ref={containerRef}
      style={{
        display: 'inline-block',
        lineHeight: 0,
        ...style,
      }}
      className={className}
      {...rest}
    >
      {children}
    </div>
  );
};

export default SvgIcon;
